import { Button, Col, Empty, Row, Table, Checkbox } from 'antd';
import * as React from 'react';
import { useEffect, useMemo, useCallback, useState } from 'react';
import { useHistory } from 'react-router';
import CustomFilterDropdown from '../../../components/CustomFilterDropdown';
import { BaseCompanyProduct, Company } from '../../../models/company-interface';
import { Feature } from '../../../models/product-interface';
import { TableAndExcelColumn } from '../../../utilites/exportToExcel';
import { HasRight, Rights } from '../../../utilites/auth';

type Filter = {
  text: string;
  value: number | string;
};

const parentFilter: Filter[] = [
  {
    value: 'Parent',
    text: 'Parent',
  },
  {
    value: 'Child',
    text: 'Child',
  },
];

const boolFilters: Filter[] = [
  {
    value: 'Yes',
    text: 'Yes',
  },
  {
    value: 'No',
    text: 'No',
  },
];

type Props = {
  companies: Company[];
  parentChild: boolean;
  feature: boolean;
  excludeDemo: boolean;
  setExcelColumns(columns: TableAndExcelColumn<Company>[]): void;
  setFilteredData(dataType: string, newFilteredData: Company[]): void;
  allowDeletion: boolean;
  selectedRowKeys: number[];
  setSelectedRowKeys: (selectedRowKeys: number[]) => void;
};

const CompanyResultsTab: React.FC<Props> = ({
  companies,
  parentChild,
  feature,
  excludeDemo,
  setExcelColumns,
  setFilteredData,
  allowDeletion,
  selectedRowKeys,
  setSelectedRowKeys
}) => {

  const history = useHistory();

  const parentName = useCallback(
    (companyId: number) => companies.find((x) => x.id === companyId)?.name,
    [companies]
  );

  const featureDescription = (feature: Feature) => 
    `${feature.name}${
      !feature.allowLimit
        ? ''
        : feature.allowLimit && feature.recordLimit === 0
        ? ' - Unlimited'
        : ` - Limit ${feature.recordLimit}`
    }`;

  const parentOrChild = (record : Company) => !!record.parentCompanyId ? 'Child' : 'Parent'
  
  const yesOrNo = (data: boolean) => !!data ? 'Yes' : 'No'
  const columns: TableAndExcelColumn<Company>[] = useMemo(
    () => [
      {
        title: 'Company ID',
        dataIndex: 'id',
        key: 'id',
        visible: true,
        width: '150px',
        render: (id: number, record: Company) => {
          return id;
        },
        sorter: {
          compare: (a: Company, b: Company) =>
            a.id === b.id ? 0 : a.id > b.id ? -1 : 1,
        },
        excelHeader: 'Company ID',
        excelRender: (record: Company) => record.id?.toString(),
      },
      {
        title: 'Name',
        dataIndex: 'name',
        key: 'name',
        visible: true,
        render: (name: string, record: Company) => {
          return (
            <Button
              type='link'
              href={`/admin/company/${record.id}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/company/${record.id}`);
              }}
            >
              {name}
            </Button>
          );
        },
        sorter: {
          compare: (a: Company, b: Company) => a.name.localeCompare(b.name),
        },
        excelHeader: 'Name',
        excelRender: (record: Company) => record.name,
      },
      {
        title: 'Matching Features',
        dataIndex: 'features',
        key: 'features',
        visible: feature,
        render: (features: Feature[] | null, record: Company) => {
          return condensedFeatures(features ?? []).map((f) => (
            <Row>
              <Col span={24}>{featureDescription(f)}</Col>
            </Row>
          ));
        },
        excelHeader: 'Matching Features',
        excelRender: (record: Company) =>
          condensedFeatures(record.features ?? [])
            .map((f) => featureDescription(f))
            .join(', '),
      },
      {
        title: 'Type',
        dataIndex: 'parentCompanyId',
        key: 'type',
        visible: parentChild,
        render: (parentCompanyId: number | null, record: Company) => {
          return parentOrChild(record);
        },
        filters: parentFilter,
        onFilter: (value: string | any, record: Company) => {
          let parent = parentOrChild(record);
          return parent === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='activeFilter'
          />
        ),
        sorter: {
          compare: (a: Company, b: Company) => {
            let parent1 = parentOrChild(a).toLowerCase();
            let parent2 = parentOrChild(b).toLowerCase();
            return parent1.localeCompare(parent2);
          },
        },
        excelHeader: 'Type',
        excelRender: (record: Company) => parentOrChild(record),
      },
      {
        title: 'Parent',
        dataIndex: 'parentCompanyId',
        key: 'parent',
        visible: parentChild,
        render: (parentCompanyId: number | null, record: Company) => {
          return !!record.parentCompanyId ? (
            <Button
              type='link'
              href={`/admin/company/${record.parentCompanyId}`}
              onClick={(e) => {
                e.preventDefault();
                history.push(`/admin/company/${record.id}`);
              }}
            >
              {parentName(record.parentCompanyId)}
            </Button>
          ) : null;
        },
        sorter: {
          compare: (a: Company, b: Company) =>
            a.baseProduct.product.name.localeCompare(
              b.baseProduct.product.name
            ),
        },
        excelHeader: 'Parent',
        excelRender: (record: Company) =>
          !!record.parentCompanyId ? parentName(record.parentCompanyId) ?? '' : '',
      },
      {
        title: 'Base Product',
        dataIndex: 'baseProduct',
        key: 'baseProduct',
        visible: true,
        render: (baseProduct: BaseCompanyProduct, record: Company) => {
          return baseProduct?.product.name;
        },
        sorter: {
          compare: (a: Company, b: Company) =>
            (a.baseProduct?.product.name ?? "").localeCompare(
              (b.baseProduct?.product.name ?? "")
            ),
        },
        excelHeader: 'Base Product',
        excelRender: (record: Company) => record.baseProduct?.product?.name ?? '',
      },
      {
        title: 'Demo Company',
        dataIndex: 'isDemoCompany',
        key: 'isDemoCompany',
        visible: !excludeDemo,
        render: (isDemoCompany: boolean, record: Company) => {
          return yesOrNo(isDemoCompany);
        },
        filters: boolFilters,
        onFilter: (value: string | any, record: Company) => {
          let active = yesOrNo(record.isDemoCompany);
          return active === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='activeFilter'
          />
        ),
        sorter: {
          compare: (a: Company, b: Company) =>
            a.confirmed === b.confirmed ? 0 : a.confirmed ? -1 : 1,
        },
        excelHeader: 'Active',
        excelRender: (record: Company) => yesOrNo(record.confirmed),
      },
      {
        title: 'Active',
        dataIndex: 'confirmed',
        key: 'confirmed',
        visible: true,
        render: (confirmed: boolean, record: Company) => {
          return yesOrNo(confirmed);
        },
        filters: boolFilters,
        onFilter: (value: string | any, record: Company) => {
          let active = yesOrNo(record.confirmed);
          return active === value;
        },
        filterDropdown: ({
          setSelectedKeys,
          selectedKeys,
          confirm,
          clearFilters,
          filters,
        }) => (
          <CustomFilterDropdown
            filters={filters}
            setSelectedKeys={setSelectedKeys}
            selectedKeys={selectedKeys}
            confirm={confirm}
            clearFilters={clearFilters}
            dataIndex='activeFilter'
          />
        ),
        sorter: {
          compare: (a: Company, b: Company) =>
            a.confirmed === b.confirmed ? 0 : a.confirmed ? -1 : 1,
        },
        excelHeader: 'Active',
        excelRender: (record: Company) => yesOrNo(record.confirmed),
      },
      /*
      {
        title: 'Meeting Date',
        dataIndex: 'meetingDate',
        key: 'meetingDate',
        render: (meetingDate: string) => {
          return meetingDate !== null && !!dateSettingId ? ShortDateStringForUser(meetingDate, dateSettingId) : null
        },
        defaultSortOrder: 'ascend' as SortOrder,
        sorter: {
          compare: (a: CommitteeMeeting, b: CommitteeMeeting) => {
            let d1 = a.meetingDate ?? new Date("01/01/1000");
            let d2 = b.meetingDate ?? new Date("01/01/1000");
            return moment(d1).diff(moment(d2));
          }
        },
      },*/
    ],
    [feature, history, parentChild, parentName]
  );

  useEffect(() => {
    setExcelColumns(columns);
  }, [columns, setExcelColumns]);

  const condensedFeatures = (features: Feature[]): Feature[] => {
    var condensed: Feature[] = [];
    features.forEach((pf) => {
      if (!condensed.some((f) => f.id === pf.id)) {
        condensed.push(pf);
      } else {
        var existing = features.find((f) => f.id === pf.id);

        if (
          !!existing &&
          existing.recordLimit > 0 &&
          (pf.recordLimit === 0 || pf.recordLimit > existing.recordLimit)
        ) {
          if (pf.recordLimit !== 0 && existing.recordLimit !== 0) {
            existing.recordLimit = existing.recordLimit + pf.recordLimit;
          } else {
            existing.recordLimit = pf.recordLimit;
          }
        }
      }
    });

    return condensed;
  };

  const onSelectChange = (newSelectedRowKeys: number[]) => {
    setSelectedRowKeys(newSelectedRowKeys);
  };

  return (<>
    <Table
      columns={columns.filter((x) => x.visible)}
      dataSource={companies}
      locale={{
        emptyText: (
          <Empty
            image={Empty.PRESENTED_IMAGE_SIMPLE}
            description={`No companies found`}
          />
        ),
      }}
      scroll={{ x: 715 }}
      size='middle'
      rowKey={(val) => val.id}
      pagination={{ defaultPageSize: 20 }}
      onChange={(pagigation, filters, sorter, extra) =>
        setFilteredData('companies', extra.currentDataSource)
      }
      rowSelection={
        (allowDeletion && HasRight([Rights.CSSuperUser]))
          ? {
              selectedRowKeys: selectedRowKeys,
              onChange: onSelectChange,
            }
          : undefined
      }
    /></>
  );
};

export default CompanyResultsTab;
